import React, { useEffect } from 'react'
import { number, string, shape, func } from 'prop-types'

import cleanUpNumbers from '@client/utils/sanitizer'
import getFullAddress from '@client/utils/fullAddress'

import useMagicLink from '@hooks/useMagicLink'
import useContact from '@hooks/useContact'
import useSimulationPermission from '@hooks/useSimulationPermission'
import useLoading from '@hooks/useLoading'
import useInternationalization from '@hooks/useInternationalization'
import useCalculateDiscount from '@hooks/useCalculateDiscount'

import { openChat } from '@client/utils/chatbot'

import * as Sections from './sections'

import CheckoutWrapper from './Checkout.style'

const DEFAULT_DEPARTMENT = 'Human Resources'

export default function Checkout({
  step,
  path,
  actions,
  onLoad,
  onNext,
  onPrevious,
}) {
  const { setFunnelStage, setDataLayer } = actions
  const { country } = useInternationalization()
  const { hasMagicLink } = useMagicLink()

  if (!hasMagicLink) {
    useSimulationPermission()
  }

  const { contact } = useContact()
  const { startLoad, finishLoad } = useLoading()

  useCalculateDiscount(contact, setFunnelStage, 'checkout')

  useEffect(() => {
    if (onLoad) {
      onLoad(contact)
    }
    openChat(30000)
  }, [])

  async function handleForm(values) {
    startLoad()

    const { email, ...restFormData } = values

    const data = {
      ...restFormData,
      benefitManagerDocument: cleanUpNumbers(values.benefitManagerDocument),
      benefitManagerName: `${values.firstName} ${values.lastName}`,
      benefitManagerEmail: `${values.email}`,
      mobilePhone: cleanUpNumbers(values.mobilePhone),
      cnpj: cleanUpNumbers(values.cnpj),
      companyPhone: cleanUpNumbers(values.companyPhone),
      fullAddress: getFullAddress(country, values),
      zip: cleanUpNumbers(values.zip),
      id: contact.id,
      funnelStage: 'signed_contract',
    }

    await setFunnelStage(data)

    setDataLayer(step, path, {
      actorData: {
        email: contact.email,
        role: contact.role,
        department: DEFAULT_DEPARTMENT,
      },
      companyData: {
        employeesNumber: contact.employeesNumber,
        industry: contact.industry,
        state: contact.state,
      },
      transactionData: {
        offerValue: contact.proposalValue,
      },
    })

    finishLoad()

    if (onNext) {
      onNext()
    }
  }

  return (
    <CheckoutWrapper>
      <Sections.Appbar />
      <Sections.Main handleForm={handleForm} onPrevious={onPrevious} />
    </CheckoutWrapper>
  )
}

Checkout.propTypes = {
  path: string.isRequired,
  actions: shape({
    setFunnelStage: func,
    setDataLayer: func,
  }).isRequired,
  step: number.isRequired,
  onLoad: func,
  onNext: func,
  onPrevious: func,
}

Checkout.defaultProps = {
  onLoad: undefined,
  onNext: undefined,
  onPrevious: undefined,
}
